import React, { useState, useEffect } from 'react';
import { db, storage } from '../../utils/firebase';
import { collection, addDoc, getDocs, serverTimestamp, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../context/AuthContext';
import { Product } from '../../types';
import { isVaultItem } from '../../utils/productWindows';
import { Plus, Upload, Loader2, LogOut, Trash2, Pencil, X, Eye } from 'lucide-react';

const AdminVault: React.FC = () => {
  const { logout } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  // The full product being edited — updates must not clobber fields this
  // form doesn't manage (a closed fragment keeps its tier, price, window)
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  // Form State
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState('0'); // Often irrelevant for Vault, but keeping for schema
  const [artistName, setArtistName] = useState('');
  const [editionNumber, setEditionNumber] = useState('');
  const [totalEditions, setTotalEditions] = useState('');
  const [releaseDate, setReleaseDate] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState('');

  // Curated piece details — presented gallery-placard style on /vault/:id
  const [medium, setMedium] = useState('');
  const [dimensions, setDimensions] = useState('');
  const [provenance, setProvenance] = useState('');
  const [description, setDescription] = useState('');
  const [artistBio, setArtistBio] = useState('');
  const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
  const [existingGallery, setExistingGallery] = useState<string[]>([]);

  const fetchVaultProducts = async () => {
    // Everything the public vault shows is manageable here: closed fragments
    // (vault membership by window mechanics) plus legacy sold_out items.
    // Fetched without a composite-index query, filtered client-side.
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'products'));
      const productList: Product[] = [];
      const now = new Date();
      querySnapshot.forEach((docSnap) => {
        const product = { id: docSnap.id, ...docSnap.data() } as Product;
        const isLegacyVault = product.status === 'sold_out' && product.tier !== 'artifact';
        if (isVaultItem(product, now) || isLegacyVault) {
          productList.push(product);
        }
      });
      // Most recently closed / created first — mirrors the public vault
      productList.sort((a, b) => {
        const key = (p: Product) => p.windowCloseAt || (p.createdAt?.toDate?.()?.toISOString() ?? '');
        return key(b).localeCompare(key(a));
      });
      setProducts(productList);
    } catch (e) {
      console.error('Error fetching vault products', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVaultProducts();
  }, []);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const resetForm = () => {
    setTitle('');
    setArtistName('');
    setEditionNumber('');
    setTotalEditions('');
    setReleaseDate('');
    setImageFile(null);
    setExistingImageUrl('');
    setMedium('');
    setDimensions('');
    setProvenance('');
    setDescription('');
    setArtistBio('');
    setGalleryFiles([]);
    setExistingGallery([]);
    setEditingProduct(null);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setTitle(product.title);
    setArtistName(product.artistName || '');
    setEditionNumber(product.editionNumber?.toString() || '');
    setTotalEditions(product.totalEditions?.toString() || '');
    setReleaseDate(product.releaseDate || '');
    setExistingImageUrl(product.image);
    setMedium(product.medium || '');
    setDimensions(product.dimensions || '');
    setProvenance(product.provenance || '');
    setDescription(product.description || '');
    setArtistBio(product.artistBio || '');
    setGalleryFiles([]);
    setExistingGallery(product.gallery || []);
    // Scroll to top to see form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this item? This action cannot be undone.')) {
        try {
            await deleteDoc(doc(db, 'products', id));
            setProducts(products.filter(p => p.id !== id));
            if (editingProduct?.id === id) resetForm();
        } catch (error) {
            console.error('Error deleting document: ', error);
            alert('Error deleting item');
        }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || (!imageFile && !existingImageUrl)) {
      alert('Please fill in title and ensure an image is set.');
      return;
    }

    setUploading(true);

    try {
      let imageUrl = existingImageUrl;

      // 1. Upload Image if new file selected
      if (imageFile) {
        const storageRef = ref(storage, `vault/${Date.now()}_${imageFile.name}`);
        await uploadBytes(storageRef, imageFile);
        imageUrl = await getDownloadURL(storageRef);
      }

      // Upload any newly added gallery images and append to the existing set
      const galleryUrls = [...existingGallery];
      for (const file of galleryFiles) {
        const galleryRef = ref(storage, `vault/${Date.now()}_${file.name}`);
        await uploadBytes(galleryRef, file);
        galleryUrls.push(await getDownloadURL(galleryRef));
      }

      // 2. Fields this form manages — the only ones an update may touch
      const curatedFields: any = {
        title,
        image: imageUrl,
        artistName,
        editionNumber: isNaN(Number(editionNumber)) ? editionNumber : Number(editionNumber),
        totalEditions: Number(totalEditions) || 0,
        releaseDate,
        medium,
        dimensions,
        provenance,
        description,
        artistBio,
        gallery: galleryUrls,
      };

      if (editingProduct) {
        // Update only the curated fields — a closed fragment keeps its tier,
        // price, status and window data. hoverImage tracks the main image
        // only where it did before (or for vault-native items).
        if (
          editingProduct.category === 'Vault' ||
          !editingProduct.hoverImage ||
          editingProduct.hoverImage === editingProduct.image
        ) {
          curatedFields.hoverImage = imageUrl;
        }
        await updateDoc(doc(db, 'products', editingProduct.id), curatedFields);
        alert('Vault item updated successfully!');
      } else {
        // New vault-native archive entry
        await addDoc(collection(db, 'products'), {
          ...curatedFields,
          price: parseFloat(price) || 0,
          category: 'Vault',
          hoverImage: imageUrl,
          status: 'sold_out',
          createdAt: serverTimestamp(),
        });
        alert('Vault item archived successfully!');
      }

      // 3. Reset and Refresh
      resetForm();
      fetchVaultProducts();

    } catch (error) {
      console.error('Error saving vault item:', error);
      alert('Error saving vault item.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-8 border-b pb-4 border-gray-200">
        <h2 className="text-3xl font-heading">VAULT MANAGEMENT</h2>
        <button
          onClick={() => logout()}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-black font-body"
        >
          <LogOut size={16} /> SIGN OUT
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* ADD/EDIT FORM */}
        <div className="bg-white p-6 rounded-sm shadow-sm border border-gray-100 h-fit">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-heading text-xl flex items-center gap-2">
              {editingProduct ? <Pencil size={20} /> : <Plus size={20} />}
              {editingProduct ? 'EDIT ITEM' : 'ARCHIVE NEW ITEM'}
            </h3>
            {editingProduct && (
                <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                    <X size={20} />
                </button>
            )}
          </div>

          {editingProduct && editingProduct.category !== 'Vault' && (
            <p className="mb-4 text-xs text-gray-500 bg-gray-50 border border-gray-200 p-2 font-body">
              This is a closed fragment. Its price, tier and window data are
              kept — only the curated details below are updated.
            </p>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">TITLE</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-gray-50"
                placeholder="Ex: Ghost Rider"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
               <div>
                  <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">ARTIST NAME</label>
                  <input
                    type="text"
                    value={artistName}
                    onChange={(e) => setArtistName(e.target.value)}
                    className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-gray-50"
                  />
               </div>
               <div>
                  <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">RELEASE DATE</label>
                  <input
                    type="date"
                    value={releaseDate}
                    onChange={(e) => setReleaseDate(e.target.value)}
                    className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-gray-50"
                  />
               </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
               <div>
                  <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">EDITION NUMBER</label>
                  <input
                    type="text"
                    value={editionNumber}
                    onChange={(e) => setEditionNumber(e.target.value)}
                    className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-gray-50"
                    placeholder="e.g. 1 or AP"
                  />
               </div>
               <div>
                  <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">TOTAL EDITIONS</label>
                  <input
                    type="number"
                    value={totalEditions}
                    onChange={(e) => setTotalEditions(e.target.value)}
                    className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-gray-50"
                  />
               </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
               <div>
                  <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">MEDIUM</label>
                  <input
                    type="text"
                    value={medium}
                    onChange={(e) => setMedium(e.target.value)}
                    className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-gray-50"
                    placeholder="e.g. Acrylic on reclaimed deck"
                  />
               </div>
               <div>
                  <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">DIMENSIONS</label>
                  <input
                    type="text"
                    value={dimensions}
                    onChange={(e) => setDimensions(e.target.value)}
                    className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-gray-50"
                    placeholder="e.g. 80 × 20 cm"
                  />
               </div>
            </div>

            <div>
              <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">CURATORIAL NOTE</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-gray-50"
                placeholder="The story of the piece — what it is, where it came from, why it's held."
              />
            </div>

            <div>
              <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">PROVENANCE</label>
              <textarea
                value={provenance}
                onChange={(e) => setProvenance(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-gray-50"
                placeholder="Its documented history — one line per stage."
              />
            </div>

            <div>
              <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">ABOUT THE ARTIST</label>
              <textarea
                value={artistBio}
                onChange={(e) => setArtistBio(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-gray-50"
                placeholder="A short artist bio."
              />
            </div>

            <div>
              <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">IMAGE</label>
              <div className="border border-dashed border-gray-300 p-4 text-center cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors relative">
                <input
                  type="file"
                  onChange={handleImageChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  accept="image/*"
                />
                <div className="flex flex-col items-center justify-center gap-2">
                  <Upload size={20} className="text-gray-400" />
                  <span className="text-xs text-gray-500 font-body">
                    {imageFile ? imageFile.name : (existingImageUrl ? 'Change Image' : 'Click to upload')}
                  </span>
                </div>
              </div>
              {existingImageUrl && !imageFile && (
                  <div className="mt-2 text-xs text-gray-400">
                      Current image set.
                  </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">ADDITIONAL IMAGES</label>
              {existingGallery.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {existingGallery.map((url) => (
                    <div key={url} className="relative w-14 h-14">
                      <img src={url} alt="" className="w-14 h-14 object-cover bg-gray-100" />
                      <button
                        type="button"
                        onClick={() => setExistingGallery(existingGallery.filter(u => u !== url))}
                        className="absolute -top-1.5 -right-1.5 bg-black text-white rounded-full p-0.5 hover:bg-red-600"
                        title="Remove image"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="border border-dashed border-gray-300 p-4 text-center cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors relative">
                <input
                  type="file"
                  multiple
                  onChange={(e) => setGalleryFiles(e.target.files ? Array.from(e.target.files) : [])}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  accept="image/*"
                />
                <div className="flex flex-col items-center justify-center gap-2">
                  <Upload size={20} className="text-gray-400" />
                  <span className="text-xs text-gray-500 font-body">
                    {galleryFiles.length > 0
                      ? `${galleryFiles.length} image${galleryFiles.length > 1 ? 's' : ''} selected`
                      : 'Click to add detail shots'}
                  </span>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={uploading}
              className="w-full bg-black text-white py-3 font-heading tracking-widest hover:bg-gray-800 transition-colors disabled:opacity-50 flex justify-center items-center"
            >
              {uploading ? <Loader2 className="animate-spin" size={20} /> : (editingProduct ? 'UPDATE ITEM' : 'ARCHIVE TO VAULT')}
            </button>

            {editingProduct && (
                <button
                    type="button"
                    onClick={resetForm}
                    className="w-full border border-gray-300 text-gray-600 py-2 font-heading tracking-widest hover:bg-gray-50 transition-colors text-xs"
                >
                    CANCEL EDIT
                </button>
            )}
          </form>
        </div>

        {/* LIST Vault Items */}
        <div className="col-span-1 lg:col-span-2">
           <h3 className="font-heading text-xl mb-6">VAULT ITEMS</h3>
           <div className="bg-white border border-gray-200">
              <table className="w-full text-left">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="p-4 font-heading text-sm text-gray-600">IMAGE</th>
                    <th className="p-4 font-heading text-sm text-gray-600">DETAILS</th>
                    <th className="p-4 font-heading text-sm text-gray-600 text-right">EDITION</th>
                    <th className="p-4 font-heading text-sm text-gray-600 text-right">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {products.map((product) => (
                    <tr key={product.id} className={`hover:bg-gray-50 ${editingProduct?.id === product.id ? 'bg-blue-50/50' : ''}`}>
                      <td className="p-4 w-24">
                        <img
                          src={product.image}
                          alt={product.title}
                          className="w-16 h-16 object-cover bg-gray-100 grayscale"
                        />
                      </td>
                      <td className="p-4">
                        <h4 className="font-heading text-lg">{product.title}</h4>
                        <span className="text-xs text-gray-500 uppercase tracking-wider block">
                          {product.tier === 'fragment'
                            ? `Fragment${product.fragmentNumber ? ` ${product.fragmentNumber}` : ''} — window closed`
                            : `Artist: ${product.artistName || 'N/A'}`}
                        </span>
                        {product.releaseDate && (
                          <span className="text-xs text-gray-400">Released: {product.releaseDate}</span>
                        )}
                      </td>
                      <td className="p-4 text-right font-mono text-sm text-gray-800">
                        {(product.editionNumber || product.totalEditions)
                          ? `${product.editionNumber ?? '—'} / ${product.totalEditions ?? '—'}`
                          : '—'}
                      </td>
                      <td className="p-4 text-right">
                          <div className="flex justify-end gap-2">
                            <a
                                href={`/vault/${product.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 text-gray-400 hover:text-green-700 hover:bg-green-50 rounded"
                                title="View on site"
                            >
                                <Eye size={18} />
                            </a>
                            <button
                                onClick={() => handleEdit(product)}
                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                title="Edit"
                            >
                                <Pencil size={18} />
                            </button>
                            <button
                                onClick={() => handleDelete(product.id)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                title="Delete"
                            >
                                <Trash2 size={18} />
                            </button>
                          </div>
                      </td>
                    </tr>
                  ))}
                  {loading && (
                    <tr>
                      <td colSpan={4} className="p-8 text-center text-gray-500 font-body">
                        <Loader2 className="animate-spin inline" size={20} />
                      </td>
                    </tr>
                  )}
                  {!loading && products.length === 0 && (
                    <tr>
                      <td colSpan={4} className="p-8 text-center text-gray-500 font-body">
                        No vault items found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
           </div>
        </div>
      </div>
    </div>
  );
};

export default AdminVault;
